/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { createBrowserRouter, Navigate } from 'react-router-dom';
import { type ReactNode, useEffect } from 'react';

import { SpaceSubModuleEnum } from '@adp-foundation/space-ui-adapter';
import { GlobalError } from '@adp-foundation/layout';
import { useSystemConfig } from '@adp-foundation/global-store';
import { BaseEnum } from '@adp-arch/web-context';

import { Layout } from '../layout';
import { UserTypeRedirect } from './user-type-redirect';
import { resolveSameOriginRedirectURL } from './redirect-url';
import {
  LoginPage,
  SpaceLayout,
  SpaceIdLayout,
  Develop,
  AgentIDELayout,
  AgentIDE,
  AgentPublishPage,
  Redirect,
  spaceSubMenu,
  exploreSubMenu,
  WorkflowPage,
  SearchPage,
  ProjectIDE,
  ProjectIDEPublish,
  Library,
  PluginLayout,
  PluginToolPage,
  PluginPage,
  KnowledgePreview,
  KnowledgeUpload,
  DatabaseDetail,
  ExplorePluginPage,
  ExploreSkillPage,
  ExploreTemplatePage,
  ExploreStorePage,
  StoreAgentPage,
  TeamManagementPage,
  ChatHistoryPage,
  LogManagementPage,
  TokenUsagePage,
  DocumentCenterPage,
} from './async-components';

const SkillFeatureRoute = ({ children }: { children: ReactNode }) => {
  const { initialized, enableSkill } = useSystemConfig();
  if (!initialized) {
    return null;
  }
  return enableSkill ? children : <Navigate to="/explore/store" replace />;
};

const ExternalAppReload = ({
  preserveURL = false,
}: {
  preserveURL?: boolean;
}) => {
  useEffect(() => {
    // /app owns its query parameters; hand the complete URL back to the server.
    if (preserveURL) {
      window.location.reload();
      return;
    }
    const redirectURL = resolveSameOriginRedirectURL(
      new URLSearchParams(window.location.search).get('redirecturl'),
      window.location.href,
    );
    if (redirectURL) {
      window.location.replace(redirectURL);
      return;
    }
    window.location.reload();
  }, [preserveURL]);

  return null;
};

export const router: ReturnType<typeof createBrowserRouter> =
  createBrowserRouter([
    // Document routing
    {
      path: '/open/docs/*',
      Component: Redirect,
      loader: () => ({
        hasSider: false,
        requireAuth: false,
      }),
    },

    {
      path: '/information/auth/success',
      Component: Redirect,
      loader: () => ({
        hasSider: false,
        requireAuth: false,
      }),
    },
    // main application route
    {
      path: '/',
      Component: Layout,
      errorElement: <GlobalError />,
      children: [
        {
          index: true,
          Component: UserTypeRedirect,
        },
        // login page routing
        {
          path: 'sign',
          Component: LoginPage,
          errorElement: <GlobalError />,
          loader: () => ({
            hasSider: false,
            requireAuth: false,
          }),
        },
        {
          path: 'app/*',
          element: <ExternalAppReload preserveURL />,
          loader: () => ({
            hasSider: false,
            requireAuth: false,
          }),
        },
        {
          path: 'agents/*',
          Component: ExternalAppReload,
          loader: () => ({
            hasSider: false,
            requireAuth: false,
          }),
        },
        {
          path: 'notebooks/*',
          Component: ExternalAppReload,
          loader: () => ({
            hasSider: false,
            requireAuth: false,
          }),
        },
        {
          path: 'space',
          Component: SpaceLayout,
          loader: () => ({
            hasSider: true,
            requireAuth: true,
            subMenu: spaceSubMenu,
            menuKey: BaseEnum.Space,
          }),
          children: [
            {
              path: ':space_id',
              Component: SpaceIdLayout,
              children: [
                {
                  index: true,
                  element: <Navigate to="develop" replace />,
                },

                // Project Development
                {
                  path: 'develop',
                  Component: Develop,
                  loader: () => ({
                    subMenuKey: SpaceSubModuleEnum.DEVELOP,
                  }),
                },

                // Agent IDE
                {
                  path: 'bot/:bot_id',
                  Component: AgentIDELayout,
                  children: [
                    {
                      index: true,
                      Component: AgentIDE,
                    },
                    {
                      path: 'publish',
                      children: [
                        {
                          index: true,
                          Component: AgentPublishPage,
                          loader: () => ({
                            hasSider: false,
                            requireBotEditorInit: false,
                            pageName: 'publish',
                          }),
                        },
                      ],
                    },
                  ],
                  loader: () => ({
                    hasSider: false,
                    showMobileTips: true,
                    requireBotEditorInit: true,
                    pageName: 'bot',
                  }),
                },

                // Project IDE
                {
                  path: 'project-ide/:project_id/publish',
                  loader: () => ({
                    hasSider: false,
                  }),
                  Component: ProjectIDEPublish,
                },
                {
                  path: 'project-ide/:project_id/*',
                  Component: ProjectIDE,
                  loader: () => ({
                    hasSider: false,
                  }),
                },

                // resource library
                {
                  path: 'library',
                  Component: Library,
                  loader: () => ({
                    subMenuKey: SpaceSubModuleEnum.LIBRARY,
                  }),
                },

                // Team Management
                {
                  path: 'team',
                  Component: TeamManagementPage,
                  loader: () => ({
                    subMenuKey: SpaceSubModuleEnum.TEAM,
                  }),
                },

                // Chat History
                {
                  path: 'chat_history',
                  Component: ChatHistoryPage,
                  loader: () => ({
                    subMenuKey: SpaceSubModuleEnum.CHAT_HISTORY,
                  }),
                },

                // Log Management
                {
                  path: 'log_management',
                  Component: LogManagementPage,
                  loader: () => ({
                    subMenuKey: SpaceSubModuleEnum.CHAT_HISTORY,
                  }),
                },
                // Token Usage
                {
                  path: 'token_usage',
                  Component: TokenUsagePage,
                  loader: () => ({
                    subMenuKey: SpaceSubModuleEnum.TOKEN_USAGE,
                  }),
                },

                // Knowledge Base Resources
                {
                  path: 'knowledge',
                  children: [
                    {
                      path: ':dataset_id',
                      element: <KnowledgePreview />,
                    },
                    {
                      path: ':dataset_id/upload',
                      element: <KnowledgeUpload />,
                    },
                  ],
                  loader: () => ({
                    pageModeByQuery: true,
                  }),
                },

                // database resources
                {
                  path: 'database',
                  children: [
                    {
                      path: ':table_id',
                      element: <DatabaseDetail />,
                    },
                  ],
                  loader: () => ({
                    showMobileTips: true,
                    pageModeByQuery: true,
                  }),
                },

                // plugin resources
                {
                  path: 'plugin/:plugin_id',
                  Component: PluginLayout,
                  children: [
                    {
                      index: true,
                      Component: PluginPage,
                    },
                    {
                      path: 'tool/:tool_id',
                      children: [
                        {
                          index: true,
                          Component: PluginToolPage,
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },

        // workflow routing
        {
          path: 'work_flow',
          Component: WorkflowPage,
          loader: () => ({
            hasSider: false,
            requireAuth: true,
          }),
        },

        // search
        {
          path: 'search/:word',
          Component: SearchPage,
          loader: () => ({
            hasSider: true,
            requireAuth: true,
          }),
        },

        // explore
        {
          path: 'explore',
          Component: null,
          loader: () => ({
            hasSider: true,
            requireAuth: true,
            subMenu: exploreSubMenu,
            menuKey: BaseEnum.Explore,
          }),
          children: [
            {
              index: true,
              // Default to 'store' page
              element: <Navigate to="store" replace />,
            },
            // Store - published agents from all accessible spaces
            {
              path: 'store',
              element: <ExploreStorePage />,
              loader: () => ({
                type: 'store',
              }),
            },
            // plugin store - redirect to template when disabled
            ...(ENABLE_EXPLORE_PLUGIN
              ? [
                  {
                    path: 'plugin',
                    element: <ExplorePluginPage />,
                    loader: () => ({
                      type: 'plugin',
                    }),
                  },
                ]
              : [
                  {
                    path: 'plugin',
                    element: <Navigate to="/explore/template" replace />,
                  },
                ]),
            // skill store
            {
              path: 'skill',
              element: (
                <SkillFeatureRoute>
                  <ExploreSkillPage />
                </SkillFeatureRoute>
              ),
              loader: () => ({
                type: 'skill',
              }),
            },
            // template
            {
              path: 'template',
              element: <ExploreTemplatePage />,
              loader: () => ({
                type: 'template',
              }),
            },
          ],
        },

        // Store agent detail page - chat with published agent
        {
          path: 'store/agent/:botId',
          element: <StoreAgentPage />,
          loader: () => ({
            hasSider: false,
            requireAuth: true,
          }),
        },

        // Document Center
        {
          path: 'docs',
          Component: DocumentCenterPage,
          loader: () => ({
            hasSider: true,
            requireAuth: true,
          }),
        },
        {
          path: 'docs/*',
          Component: DocumentCenterPage,
          loader: () => ({
            hasSider: true,
            requireAuth: true,
          }),
        },
      ],
    },
  ]);
